import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  FormControl,
  Autocomplete,
  Chip,
  CircularProgress,
  Box,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useConfigDataService } from "../../services/ConfigDataService";
import { useNavigate } from "react-router-dom";

import { VariableSizeList } from "react-window";

const AdminUserFormDialog = ({ open, onClose, onSubmit, Access_id = 0 }) => {
    const navigate = useNavigate();
  const {
    fetchAdminUserList,
    getEmployeeList,
    getProjectLocations,
    getBusinessVerticals,
    getEmployeeListtoAddAdmin,
    saveAdminAccessConfiguration,
  } = useConfigDataService();
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [verticalOptions, setVerticalOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [editData, setEditData] = useState(null);
  const [projectMode, setProjectMode] = useState("all");

  const { control, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: {
      full_Name: "test",
      businessVerticalList: [],
      accessId: 0,
      ampersandId: 0,
      mailiD: null,
      isAuthorized: false,
      isSuperAdmin: false,
      businessVertical: [],
      project_Location: "All",
      project_Name: [],
    },
  });
  const LISTBOX_PADDING = 8;

  const renderRow = (props) => {
    const { data, index, style } = props;
    return React.cloneElement(data[index], {
      style: {
        ...style,
        top: style.top + LISTBOX_PADDING,
      },
    });
  };

  const ListboxComponent = React.forwardRef(function ListboxComponent(
    props,
    ref
  ) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemSize = 40;

    return (
      <div ref={ref} {...other}>
        <VariableSizeList
          height={Math.min(8, itemData.length) * itemSize + 2 * LISTBOX_PADDING}
          width="100%"
          itemSize={() => itemSize}
          itemCount={itemData.length}
          itemData={itemData}
        >
          {renderRow}
        </VariableSizeList>
      </div>
    );
  });
  const [optionsReady, setOptionsReady] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    if (!open) return;

    // Load all options
    setOptionsReady(false);
    Promise.all([
      getEmployeeListtoAddAdmin().then(setEmployeeOptions),
      getBusinessVerticals().then(setVerticalOptions),
      getProjectLocations().then(setProjectOptions),
    ]).then(() => setOptionsReady(true));
  }, [open]);

  useEffect(() => {
    if (!open || !optionsReady) return;

    if (Access_id === 0) {
      // Create mode
      reset({
        full_Name: "test",
        businessVerticalList: [],
        accessId: 0,
        ampersandId: 0,
        mailiD: null,
        isAuthorized: false,
        isSuperAdmin: false,
        businessVertical: [],
        project_Location: "All",
        project_Name: [],
      });
    } else {
      // Edit mode
      setFormLoading(true); // 👉 show loader
      fetchAdminUserList().then((data) => {
        const match = data.find((x) => x.AccessId === Access_id);
        console.log(match);
        if (match) {
          const selectedEmployee = employeeOptions.find(
            (emp) => emp.Official_Email_ID === match.MailiD
          );

          reset({
            ...match,
            mailiD: selectedEmployee || null,
            isAuthorized: match.IsAuthorized ?? false,
            isSuperAdmin: match.IsSuperAdmin ?? false,
            businessVertical: match.BusinessVertical?.split(",") || [],
            project_Name: match.Project_Name?.split(",") || [],
          });

          const firstLocation = match.Project_Location?.split(",")[0]?.trim();

          let mode = "All"; // default
          if (firstLocation === "Location") {
            mode = "Location";
          } else if (firstLocation !== "All") {
            mode = "ParticularProject";
          }
          setProjectMode(mode);

          // Optional: set project_Name only if particular project mode
          if (mode === "ParticularProject") {
            setValue("project_Name", match.Project_Location?.split(",") || []);
          }
        }
        setFormLoading(false); // 👉 hide loader
      });
    }
  }, [open, Access_id, optionsReady, employeeOptions, reset, setValue]);

  const handleFormSubmit = async (formData) => {
    const selected = formData.mailiD;
    const finalPayload = {
      accessId: formData.AccessId || 0,
      mailiD: selected?.Official_Email_ID || "",
      isAuthorized: formData.isAuthorized || false,
      isSuperAdmin: formData.isSuperAdmin || false,
      ampersandId: formData.AmpersandId || 0,
      full_Name: selected?.Full_Name || "",
      EmployeeId: " ",
      businessVertical: (formData.businessVertical || []).join(","),
      businessVerticalList: formData.businessVertical || [],
      project_Location:
        projectMode === "All"
          ? "All"
          : projectMode === "Location"
          ? "Location"
          : formData.project_Name || "0",
      project_Name: " ",
    };
    try {
      console.log(finalPayload);
      setSaving(true); // You can add this loader state to your Save button too
      const response = await saveAdminAccessConfiguration(finalPayload);
      console.log("response ", response);
      if (response?.success === true) {
        showSnackbar("Saved successfully!", "success");
        setTimeout(() => {
          onSubmit(finalPayload); // close dialog after short delay
        }, 1000);
        navigate("/Admin-Access", { replace: true });
      } else {
        showSnackbar(response?.message || "Save failed.", "error");
      }
    } catch (err) {
      showSnackbar("Something went wrong. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {Access_id === 0 ? "Create Admin User" : "Edit Admin User"}
      </DialogTitle>
      <DialogContent dividers>
        {formLoading ? (
          <Box
            height={180}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress size={32} />
          </Box>
        ) : (
          <form>
            <Controller
              name="mailiD"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  disableListWrap
                  ListboxComponent={ListboxComponent}
                  options={employeeOptions}
                  getOptionLabel={(option) => option?.Full_Name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.Official_Email_ID === value?.Official_Email_ID
                  }
                  value={field.value || null}
                  onChange={(_, selected) => field.onChange(selected || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Email"
                      fullWidth
                      margin="normal"
                    />
                  )}
                />
              )}
            />

            <FormControlLabel
              control={
                <Controller
                  name="isAuthorized"
                  control={control}
                  render={({ field }) => (
                    <Checkbox {...field} checked={field.value} />
                  )}
                />
              }
              label="Is Authorized"
            />

            <FormControlLabel
              control={
                <Controller
                  name="isSuperAdmin"
                  control={control}
                  render={({ field }) => (
                    <Checkbox {...field} checked={field.value} />
                  )}
                />
              }
              label="Is Super Admin"
            />

            <Controller
              name="businessVertical"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  {...field}
                  options={verticalOptions}
                  freeSolo
                  value={field.value || []}
                  onChange={(_, value) => field.onChange(value)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const chipProps = getTagProps({ index });
                      const { key, ...rest } = chipProps;
                      return <Chip key={key} label={option} {...rest} />;
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Business Verticals"
                      fullWidth
                      margin="normal"
                    />
                  )}
                />
              )}
            />

            <FormControl fullWidth margin="normal">
              <FormLabel>Project Assignment</FormLabel>
              <RadioGroup
                row
                value={projectMode}
                onChange={(e) => setProjectMode(e.target.value)}
              >
                <FormControlLabel
                  value="All"
                  control={<Radio />}
                  label="All projects under selected Business vertical"
                />
                <FormControlLabel
                  value="Location"
                  control={<Radio />}
                  label="Location (Business sub vertical of selected user)"
                />
                <FormControlLabel
                  value="ParticularProject"
                  control={<Radio />}
                  label="Particular Project under selected Business vertical"
                />
              </RadioGroup>
            </FormControl>

            {projectMode === "ParticularProject" && (
              <Controller
                name="project_Name"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={projectOptions} // string[]
                    value={field.value || ""}
                    onChange={(_, value) => field.onChange(value || "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Project"
                        fullWidth
                        margin="normal"
                      />
                    )}
                    freeSolo
                  />
                )}
              />
            )}
          </form>
        )}
      </DialogContent>
      <DialogActions>
        {saving ? (
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            width="100%"
            justifyContent="center"
            py={1}
          >
            <CircularProgress size={24} />
            <Typography variant="body2">Saving...</Typography>
          </Box>
        ) : saveSuccess ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            py={1}
          >
            <Typography variant="body2" color="success.main">
              Saved Successfully ✅
            </Typography>
          </Box>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleSubmit(handleFormSubmit)}
              variant="contained"
              color="primary"
            >
              Save
            </Button>
          </>
        )}
      </DialogActions>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AdminUserFormDialog;
